import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@strava/ui/Button';

import Lightbox from 'react-image-lightbox';

import 'react-image-lightbox/style.css';
import NavigationMoreHighlightedXsmall from '@strava/icons/NavigationMoreHighlightedXsmall';
import ActionsEditNormalXsmall from '@strava/icons/ActionsEditNormalXsmall';

import createNetworkingClient from 'utils/networking-client';
import I18n from 'utils/I18n';

import { photoListProps } from '../../../../props/feed-prop-types';
import CaptionEditModal from './components/CaptionEditModal';
import ReportPhotoModal from './components/ReportPhotoModal';

import styles from './styles.scss';
import './lightbox.css';

const lightboxCustomStyles = {
  overlay: {
    zIndex: '4000' // Modals on top of this lightbox are set to 5000
  }
};

const StravaPhotoLightbox = ({
  selectedPhotoIndex,
  photoList,
  onCloseRequest
}) => {
  const [params, setParams] = useState({
    photoIndex: selectedPhotoIndex,
    isSavingCaption: false,
    isReportingPhoto: false,
    openEditCaptionModal: false,
    openReportPhotoModal: false,
    showReportPhotoButton: false,
    updatedCaption: '',
    reportedPhotoHash: {}
    // reported status is kept in-memory
    // on refresh, phoots-service does not include reported photos in the results
  });

  const lockWheelScrolling = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const lockKeyScrolling = (e) => {
    if (['ArrowUp', 'ArrowDown'].indexOf(e.code) > -1) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  useEffect(() => {
    if (document) {
      document.addEventListener('wheel', lockWheelScrolling, {
        passive: false
      });
      document.addEventListener('keydown', lockKeyScrolling, false);
    }
    return () => {
      if (document) {
        document.removeEventListener('wheel', lockWheelScrolling, {
          passive: false
        });
        document.removeEventListener('keydown', lockKeyScrolling, false);
      }
    };
  }, []);

  const photoCount = photoList.length;
  const prevPhotoIndex = (params.photoIndex + photoCount - 1) % photoCount;
  const nextPhotoIndex = (params.photoIndex + 1) % photoCount;

  const currentPhoto = photoList[params.photoIndex];
  const prevPhoto = photoList[prevPhotoIndex];
  const nextPhoto = photoList[nextPhotoIndex];
  const onMoveNextRequest = () =>
    setParams({
      ...params,
      photoIndex: nextPhotoIndex
    });
  const onMovePrevRequest = () =>
    setParams({
      ...params,
      photoIndex: prevPhotoIndex
    });

  const toggleReportPhotoButton = () => {
    setParams((prevState) => ({
      ...params,
      showReportPhotoButton: !prevState.showReportPhotoButton,
      openReportPhotoModal: false
    }));
  };

  const reportPhoto = (reason) => {
    setParams({ ...params, isReportingPhoto: true });
    const url = `/photos/${currentPhoto.photo_id}`;
    const payload = {
      report_reason: reason,
      id: currentPhoto.photo_id,
      owner_id: currentPhoto.owner_id
    };
    createNetworkingClient()
      .put(url, payload)
      .then(() => {
        const updatedReportedPhotoHash = params.reportedPhotoHash;
        updatedReportedPhotoHash[currentPhoto.photo_id] = true;
        // TODO: throw error in monorepo and show user a message
        setParams({
          ...params,
          isReportingPhoto: false,
          reportedPhotoHash: updatedReportedPhotoHash
        });
      })
      .catch(() => {
        // TODO: throw error in monorepo and show user a message
        const updatedReportedPhotoHash = params.reportedPhotoHash;
        updatedReportedPhotoHash[currentPhoto.photo_id] = false;
        setParams({
          ...params,
          isReportingPhoto: false,
          reportedPhotoHash: updatedReportedPhotoHash
        });
      });
  };

  const updateCaption = () => {
    setParams({ ...params, isSavingCaption: true });
    // # 1 is Strava::Phoots::Thrift::PhotoSource::STRAVA -- It seems like too much
    // # work to pass that here.  Alternatively, could pass just the UUID and let
    // # the Controller add the source.
    const url = `/photos/1:${currentPhoto.photo_id}/add_caption`;
    const payload = {
      caption: params.updatedCaption
    };
    createNetworkingClient()
      .put(url, payload)
      .then(() => {
        // Update the caption in-memory
        // eslint-disable-next-line no-param-reassign
        currentPhoto.caption_escaped = params.updatedCaption;
        // TODO: throw error in monorepo and show user a message
        setParams({
          ...params,
          isSavingCaption: false,
          openEditCaptionModal: false,
          updatedCaption: ''
        });
      })
      .catch(() => {
        // TODO: throw error in monorepo and show user a message
        setParams({
          ...params,
          isSavingCaption: false
        });
      });
  };

  const addOrEditCaptionLabel = () =>
    currentPhoto.caption_escaped
      ? I18n.t('templates.feed.activity.edit_caption_label')
      : I18n.t('templates.feed.activity.add_caption_label');

  const openAddEditCaptionModal = () => {
    setParams({
      ...params,
      openEditCaptionModal: true
    });
  };

  const openReportPhotoModal = () => {
    setParams({
      ...params,
      openReportPhotoModal: true
    });
  };

  const toolbarButtons = () => {
    const buttons = [];
    if (currentPhoto.viewing_athlete_id !== currentPhoto.owner_id)
      buttons.push(
        <div key="report-button-and-menu">
          {params.showReportPhotoButton && (
            <Button
              variant="text"
              className={styles.toolbarButton}
              onClick={openReportPhotoModal}
              data-testid="report_photo_button"
            >
              {I18n.t('templates.feed.activity.report_photo.title')}
            </Button>
          )}
          <Button
            className={`${styles.toolbarButton} ${styles.menu}`}
            variant="icon"
            onClick={toggleReportPhotoButton}
            data-testid="report_photo_menu"
          >
            <NavigationMoreHighlightedXsmall fill="white" />
          </Button>
        </div>
      );

    if (currentPhoto.editable) {
      buttons.push(
        <div key="edit_caption_button" className={styles.captionBtnContainer}>
          <Button
            variant="text"
            className={styles.toolbarButton}
            data-testid="edit-caption-toolbar-button"
            onClick={openAddEditCaptionModal}
          >
            {addOrEditCaptionLabel()}
          </Button>
        </div>
      );
    }
    return buttons;
  };

  const imageCaption = () => {
    return (
      <div className={styles.captionBox}>
        <div className={styles.side} />
        {currentPhoto.caption_escaped && (
          <div className={styles.caption}>{currentPhoto.caption_escaped}</div>
        )}
        {!currentPhoto.caption_escaped &&
          currentPhoto.viewing_athlete_id === currentPhoto.owner_id && (
            <Button
              className={styles.addCaptionBarButton}
              data-testid="add-caption-bar-button"
              onClick={openAddEditCaptionModal}
            >
              {addOrEditCaptionLabel()}
            </Button>
          )}
        <div className={styles.side}>
          {currentPhoto.caption_escaped &&
            currentPhoto.viewing_athlete_id === currentPhoto.owner_id && (
              <Button
                className={styles.editCaptionBarButton}
                data-testid="edit-caption-bar-button"
                onClick={openAddEditCaptionModal}
                variant="icon"
              >
                <ActionsEditNormalXsmall fill="white" />
              </Button>
            )}
        </div>
      </div>
    );
  };

  return (
    <>
      {currentPhoto && (
        <div data-testid="light-box">
          <CaptionEditModal
            isOpen={params.openEditCaptionModal}
            isSaving={params.isSavingCaption}
            caption={currentPhoto.caption_escaped}
            onDismiss={() =>
              setParams({ ...params, openEditCaptionModal: false })
            }
            onSubmit={updateCaption}
            onTextChange={(captionText) => {
              params.updatedCaption = captionText;
            }}
            title={addOrEditCaptionLabel()}
          />
          <ReportPhotoModal
            isReported={params.reportedPhotoHash[currentPhoto.photo_id]}
            isReporting={params.isReportingPhoto}
            isOpen={params.openReportPhotoModal}
            onDismiss={() => {
              setParams({ ...params, openReportPhotoModal: false });
              toggleReportPhotoButton();
            }}
            onSubmit={(reason) => {
              reportPhoto(reason);
            }}
          />

          <Lightbox
            key="light_box"
            mainSrc={currentPhoto.large}
            nextSrc={photoCount > 1 ? nextPhoto.large : ''} // no next photo if activity only has one photo
            prevSrc={photoCount > 1 ? prevPhoto.large : ''} // no previous photo if activity only has one photo
            onCloseRequest={onCloseRequest}
            onMovePrevRequest={onMovePrevRequest}
            onMoveNextRequest={onMoveNextRequest}
            imageCaption={imageCaption()}
            imagePadding={80}
            reactModalStyle={lightboxCustomStyles}
            toolbarButtons={toolbarButtons()}
          />
        </div>
      )}
    </>
  );
};

StravaPhotoLightbox.propTypes = {
  selectedPhotoIndex: PropTypes.number.isRequired,
  photoList: photoListProps.isRequired,
  onCloseRequest: PropTypes.func.isRequired
};

export default StravaPhotoLightbox;
